import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { unitId, actionId } from '@/annotations'
import { AuthCard } from '@/components/AuthCard'
import { AuthCardBrand } from '@/components/AuthCardBrand'
import { AuthCardLead } from '@/components/AuthCardLead'
import { OtpInput } from '@/components/OtpInput'
import { Button } from '@/components/Button'
import { Note } from '@/components/Note'
import { getApiClient, ApiProblemError, type Problem } from '@/data/apiClient'
import { setAccessToken } from '@/data/store'
import { AUTH_BRAND, A_MFA_COPY } from '@/copy/registry'
import { FormError, RateLimitedNotice, UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'

/**
 * `a-mfa` — the sign-in challenge step (`identityAnswerChallenge`). "There is no *use a recovery
 * code instead*" (family rule A2). `CHALLENGE_EXPIRED` restarts at sign-in (§3.1i rule 2/13); a
 * reload loses the continuation too, for the same reason.
 */
export function AMfa({
  continuation,
  email,
  onAnswered,
  onExpired,
  onBackToSignIn,
}: {
  continuation: string
  email: string
  onAnswered: () => void
  onExpired: () => void
  onBackToSignIn: () => void
}) {
  const [code, setCode] = useState('')

  const answer = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.POST('/auth/challenge', { body: { continuation, code } })
      if (error) throw new ApiProblemError(error as Problem)
      return data
    },
    onSuccess: (data) => {
      if (data) setAccessToken(data.access_token)
      onAnswered()
    },
    onError: (err) => {
      if (err instanceof ApiProblemError && err.problem.refusal_kind === 'CHALLENGE_EXPIRED') onExpired()
    },
  })

  const problem = answer.error instanceof ApiProblemError ? answer.error.problem : undefined

  return (
    <AuthCard unitId={unitId('a-mfa')}>
      <AuthCardBrand label={AUTH_BRAND} />
      <h3 className="m-0 mb-3 text-section font-semibold">{A_MFA_COPY.heading}</h3>
      <AuthCardLead>{A_MFA_COPY.leadTemplate(email)}</AuthCardLead>

      <OtpInput value={code} onChange={setCode} />

      {problem && <AMfaRefusal problem={problem} />}

      <Button data-act={actionId('session.answerChallenge')} disabled={answer.isPending || code.length < 6} onClick={() => answer.mutate()}>
        {answer.isPending ? 'Verifying…' : A_MFA_COPY.submitButton}
      </Button>

      <Note>{A_MFA_COPY.noRecoveryCodeNote}</Note>
      <p className="mt-14">
        <a href="#" data-act={actionId('nav.a-signin')} onClick={(e) => { e.preventDefault(); onBackToSignIn() }} className="text-link text-small">
          {A_MFA_COPY.backToSignInLabel}
        </a>
      </p>
    </AuthCard>
  )
}

function AMfaRefusal({ problem }: { problem: Problem }) {
  switch (problem.refusal_kind) {
    case 'CREDENTIAL_REJECTED':
      return <FormError>{A_MFA_COPY.rejectedCopy}</FormError>
    case 'CHALLENGE_EXPIRED':
      return <FormError>{A_MFA_COPY.expiredCopy}</FormError>
    case 'VALIDATION_FAILED':
      return <FormError>{problem.errors?.map((e) => e.message).join(' ') ?? problem.title}</FormError>
    case 'RATE_LIMITED':
      return <RateLimitedNotice problem={problem} />
    default:
      return isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <FormError>{problem.title}</FormError>
  }
}
